/**
 * WorkerService — Promise-based Web Worker manager.
 *
 * Creates a worker lazily on first use, tracks pending tasks via unique IDs,
 * and returns Promises for each dispatched task. Idle workers are terminated
 * after IDLE_TIMEOUT_MS to free resources.
 *
 * Usage:
 *   const svc = new WorkerService(() =>
 *     new Worker(new URL('./myWorker.js', import.meta.url))
 *   );
 *   const result = await svc.run('TASK_TYPE', payload);
 *   svc.terminate(); // optional — cleans up immediately
 */

const IDLE_TIMEOUT_MS = 60_000; // Terminate worker after 60s of inactivity

let taskCounter = 0;
const nextTaskId = () => `task_${++taskCounter}_${Date.now()}`;

class WorkerService {
  /**
   * @param {Function} workerFactory — A zero-arg function that returns a new Worker instance.
   *   Example: () => new Worker(new URL('./myWorker.js', import.meta.url))
   */
  constructor(workerFactory) {
    this._factory = workerFactory;
    this._worker = null;
    this._pending = new Map();
    this._idleTimer = null;
  }

  /** Lazily create the worker and attach listeners. */
  _ensureWorker() {
    if (this._worker) return;

    this._worker = this._factory();

    this._worker.onmessage = (event) => {
      const { taskId, result, error } = event.data;
      const entry = this._pending.get(taskId);
      if (!entry) return;

      this._pending.delete(taskId);

      if (error) {
        entry.reject(new Error(error));
      } else {
        entry.resolve(result);
      }

      this._resetIdleTimer();
    };

    this._worker.onerror = (err) => {
      // Reject all pending tasks
      for (const [, entry] of this._pending) {
        entry.reject(new Error(err.message || 'Worker error'));
      }
      this._pending.clear();
      this._destroyWorker();
    };
  }

  /** Start or restart the idle auto-termination timer. */
  _resetIdleTimer() {
    clearTimeout(this._idleTimer);

    if (this._pending.size === 0) {
      this._idleTimer = setTimeout(() => {
        if (this._pending.size === 0) {
          this._destroyWorker();
        }
      }, IDLE_TIMEOUT_MS);
    }
  }

  /** Terminate the underlying worker. */
  _destroyWorker() {
    clearTimeout(this._idleTimer);
    if (this._worker) {
      this._worker.terminate();
      this._worker = null;
    }
  }

  /**
   * Dispatch a task to the worker and return a Promise for its result.
   *
   * @param {string} type — Task type identifier (matched in the worker's switch).
   * @param {*} data — Serializable payload for the worker.
   * @param {Array} [transferables] — Optional transferable objects (ArrayBuffers).
   * @returns {Promise<*>} Resolves with the worker's result.
   */
  run(type, data, transferables = []) {
    this._ensureWorker();

    const taskId = nextTaskId();

    return new Promise((resolve, reject) => {
      this._pending.set(taskId, { resolve, reject });

      clearTimeout(this._idleTimer);

      this._worker.postMessage({ taskId, type, data }, transferables);
    });
  }

  /** Immediately terminate the worker and reject any pending tasks. */
  terminate() {
    for (const [, entry] of this._pending) {
      entry.reject(new Error('Worker terminated'));
    }
    this._pending.clear();
    this._destroyWorker();
  }

  /** True if the underlying worker is currently alive. */
  get isAlive() {
    return this._worker !== null;
  }

  /** Number of tasks currently in flight. */
  get pendingCount() {
    return this._pending.size;
  }
}

export default WorkerService;
